/**
 * SignatoryChips — who still has to sign, and who already has.
 *
 * One chip per party, in sign order, because sign order is the thing a driver most often
 * needs explained: "why can I not sign yet" is answered by seeing that the chip above
 * theirs is still open. The chip for the reader's own row says "You".
 */

import { Check, Circle, Clock, X } from 'lucide-react';
import { format } from 'date-fns';

import { cn } from '@/lib/utils';
import { partyLabel } from '@/lib/esign';
import type { AgreementSignatureSummary } from '@/hooks/useAgreements';

interface Props {
    signatures: AgreementSignatureSummary[] | null | undefined;
    className?: string;
}

function chipFor(signature: AgreementSignatureSummary) {
    const label = partyLabel(signature.party, signature.role_label);

    switch (signature.status) {
        case 'SIGNED': {
            const when = signature.signed_at
                ? format(new Date(signature.signed_at), 'MMM d, yyyy')
                : 'signed';
            return {
                icon: <Check className="h-3 w-3" />,
                text: `${label} · ${when}`,
                className: 'border-foreground/20 bg-foreground/5 text-foreground',
            };
        }
        case 'DECLINED':
        case 'VOIDED':
            return {
                icon: <X className="h-3 w-3" />,
                text: `${label} · ${signature.status === 'VOIDED' ? 'voided' : 'declined'}`,
                className: 'border-destructive/30 bg-destructive/5 text-destructive',
            };
        // SENT and VIEWED carry no timestamp of their own in this payload — `signed_at` is
        // null until the signature lands — so the chip says what is true and no more.
        case 'SENT':
        case 'VIEWED':
            return {
                icon: <Clock className="h-3 w-3" />,
                text: `${label} · ${signature.status === 'VIEWED' ? 'reading' : 'awaiting signature'}`,
                className: 'border-border bg-muted text-muted-foreground',
            };
        default:
            return {
                icon: <Circle className="h-3 w-3" />,
                text: `${label} · not sent`,
                className: 'border-border bg-transparent text-muted-foreground',
            };
    }
}

export default function SignatoryChips({ signatures, className }: Props) {
    const rows = [...(signatures ?? [])].sort(
        (a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0),
    );
    if (rows.length === 0) return null;

    return (
        <div className={cn('flex flex-wrap gap-1.5', className)}>
            {rows.map((signature) => {
                const chip = chipFor(signature);
                return (
                    <span
                        key={`${signature.party}-${signature.sign_order}`}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium',
                            chip.className,
                        )}
                    >
                        {chip.icon}
                        {chip.text}
                    </span>
                );
            })}
        </div>
    );
}
